import type { CSSProperties, FC } from 'react';
import React, { forwardRef, memo } from 'react';

import type { OfferData } from '@wwe-portal/entities/offer.type';
import sectionTitleBg from '@wwe-portal/ui/assets/images/section-title-store-revamp.webp';
import { DynamicImage } from '@wwe-portal/ui/components/dynamic-image';
import type { Banner } from '@wwe-portal/ui/features/offer';
import {
  OfferCategory,
  OfferGrid,
  StepupBannerConsumer,
} from '@wwe-portal/ui/features/offer';

export enum VirtualRowEnum {
  CategorySummary = 'CategorySummary',
  SectionSummary = 'SectionSummary',
  OfferGridRow = 'OfferGridRow',
}

const nextRowPaddings: Record<
  VirtualRowEnum,
  Record<VirtualRowEnum, number>
> = {
  [VirtualRowEnum.CategorySummary]: {
    [VirtualRowEnum.CategorySummary]: 24,
    [VirtualRowEnum.SectionSummary]: 20,
    [VirtualRowEnum.OfferGridRow]: 24,
  },
  [VirtualRowEnum.SectionSummary]: {
    [VirtualRowEnum.CategorySummary]: 24,
    [VirtualRowEnum.SectionSummary]: 24,
    [VirtualRowEnum.OfferGridRow]: 24,
  },
  [VirtualRowEnum.OfferGridRow]: {
    [VirtualRowEnum.CategorySummary]: 24,
    [VirtualRowEnum.SectionSummary]: 24,
    [VirtualRowEnum.OfferGridRow]: 12,
  },
};

export const getRowPaddingBottom = (
  currentType: VirtualRowEnum | undefined,
  nextType: VirtualRowEnum | undefined
) => {
  if (!currentType || !nextType) return 0;
  return nextRowPaddings[currentType][nextType];
};

export interface StoreItemVirtualRow {
  prevRowType?: VirtualRowEnum;
  nextRowType?: VirtualRowEnum;
  offerRow?: {
    offers: OfferData[];
    banner: Banner | undefined;
  };
  sectionSummary?: {
    title: string;
    description: string;
    tooltipTitle?: React.ReactNode;
    sectionImage?: string;
  };
  categorySummary?: {
    id: string;
    title: string;
    viewAs: 'section' | 'text' | undefined;
  };
  type: VirtualRowEnum;
}

const CategorySummaryRow: FC<Pick<StoreItemVirtualRow, 'categorySummary'>> =
  memo(({ categorySummary }) => {
    if (!categorySummary) return null;
    if (categorySummary.viewAs === 'text')
      return (
        <div
          data-category={categorySummary.id}
          className="flex items-center justify-between"
        >
          <div className="text-text300 text-base font-semibold md:text-xl">
            {categorySummary.title}
          </div>
        </div>
      );
    if (categorySummary.viewAs === 'section')
      return (
        <OfferCategory id={categorySummary.id} title={categorySummary.title} />
      );

    return (
      <>
        <div data-category={categorySummary.id} className="block md:hidden">
          <OfferCategory title={categorySummary.title} />
        </div>
        <div
          data-category={categorySummary.id}
          className="hidden items-center justify-between md:flex"
        >
          <div className="text-text300 text-base font-semibold md:text-xl">
            {categorySummary.title}
          </div>
        </div>
      </>
    );
  });
CategorySummaryRow.displayName = 'CategorySummaryRow';

const SectionSummaryRow: FC<Pick<StoreItemVirtualRow, 'sectionSummary'>> = memo(
  ({ sectionSummary }) => {
    if (sectionSummary == null) return null;
    return (
      <OfferCategory
        backgroundImage={
          <DynamicImage
            className="h-full w-full object-fill"
            defaultUrl={sectionTitleBg}
            dynamicUrl={sectionSummary.sectionImage}
            resizedWidth={400}
          />
        }
        description={sectionSummary.description}
        title={sectionSummary.title}
        tooltipTitle={sectionSummary.tooltipTitle}
      />
    );
  }
);
SectionSummaryRow.displayName = 'SectionSummaryRow';

const OfferGridRow: FC<Pick<StoreItemVirtualRow, 'offerRow'>> = memo(
  ({ offerRow }) => {
    if (offerRow == null) return null;
    if (offerRow.banner && offerRow.offers.length === 0) {
      const banner = offerRow.banner;
      return (
        <div className="flex items-center justify-center">
          <div className="3xl:w-1/3 w-full px-1 md:w-1/2">
            <StepupBannerConsumer
              bannerUrl={banner.bannerUrl}
              description={banner.description}
              endDate={banner.endDate}
            />
          </div>
        </div>
      );
    }
    return <OfferGrid banner={offerRow.banner} offers={offerRow.offers} />;
  }
);
OfferGridRow.displayName = 'OfferGridRow';

const ComponentMap = {
  [VirtualRowEnum.CategorySummary]: CategorySummaryRow,
  [VirtualRowEnum.SectionSummary]: SectionSummaryRow,
  [VirtualRowEnum.OfferGridRow]: OfferGridRow,
};

interface VirtualRowProps {
  style?: CSSProperties;
  index: number;
  data: StoreItemVirtualRow | undefined;
}

export const VirtualRow = memo(
  forwardRef<HTMLDivElement, VirtualRowProps>(({ data, style, index }, ref) => {
    if (data == null) return null;
    const Component = ComponentMap[data.type];
    return (
      <div
        data-index={index}
        ref={ref}
        style={{
          ...style,
          paddingBottom: getRowPaddingBottom(data.type, data.nextRowType),
        }}
      >
        <Component {...data} />
      </div>
    );
  })
);
VirtualRow.displayName = 'VirtualRow';
